import axios from "axios";
import { getEnvironment } from "../utils/getEnvironment";

const environment = getEnvironment();

interface LocationRaw {
    key: string;
    name: string;
    city: string;
    address: string;
    description: string;
    image?: string | null;
    timezone: string;
    currency: string;
    mo: boolean;
    moStart: number;
    moEnd: number;
    tu: boolean;
    tuStart: number;
    tuEnd: number;
    we: boolean;
    weStart: number;
    weEnd: number;
    th: boolean;
    thStart: number;
    thEnd: number;
    fr: boolean;
    frStart: number;
    frEnd: number;
    sa: boolean;
    saStart: number;
    saEnd: number;
    su: boolean;
    suStart: number;
    suEnd: number;
    monthlyRevenueGoal?: number | null;
    monthlyHoursGoal?: number | null;
}

export interface DayCountSummary {
    count: number; // -1 means closed
    normal?: number;
    blocked?: number;
    items?: Array<{
        id?: string | number;
        start: string; // ISO UTC
        end?: string | null;
        state: string;
        isBlocked?: boolean; // true for private/blocked
    }>;
}

export default class Location {
    key: string;
    name: string;
    city: string;
    address: string;
    description: string;
    image?: string | null;
    timezone: string;
    currency: string;
    mo: boolean;
    moStart: number;
    moEnd: number;
    tu: boolean;
    tuStart: number;
    tuEnd: number;
    we: boolean;
    weStart: number;
    weEnd: number;
    th: boolean;
    thStart: number;
    thEnd: number;
    fr: boolean;
    frStart: number;
    frEnd: number;
    sa: boolean;
    saStart: number;
    saEnd: number;
    su: boolean;
    suStart: number;
    suEnd: number;
    monthlyRevenueGoal?: number | null;
    monthlyHoursGoal?: number | null;

    constructor(data: LocationRaw) {
        this.key = data.key;
        this.name = data.name;
        this.city = data.city;
        this.address = data.address;
        this.description = data.description;
        this.timezone = data.timezone;
        this.currency = data.currency;
        this.mo = data.mo;
        this.moStart = data.moStart;
        this.moEnd = data.moEnd;
        this.tu = data.tu;
        this.tuStart = data.tuStart;
        this.tuEnd = data.tuEnd;
        this.we = data.we;
        this.weStart = data.weStart;
        this.weEnd = data.weEnd;
        this.th = data.th;
        this.thStart = data.thStart;
        this.thEnd = data.thEnd;
        this.fr = data.fr;
        this.frStart = data.frStart;
        this.frEnd = data.frEnd;
        this.sa = data.sa;
        this.saStart = data.saStart;
        this.saEnd = data.saEnd;
        this.su = data.su;
        this.suStart = data.suStart;
        this.suEnd = data.suEnd;
        this.image = data.image;
        this.monthlyRevenueGoal = data.monthlyRevenueGoal;
        this.monthlyHoursGoal = data.monthlyHoursGoal;
    }

    public static readonly list = async (uuid: string, brand: string) =>
        (
            (await axios
                .get(environment.api_url + "/" + brand + "/location/list", {
                    headers: {
                        "User-Token": uuid,
                    },
                })
                .then((res) => (res.status === 200 ? res.data : undefined))
                .catch(console.error)) as LocationRaw[] | undefined
        )?.map((x) => new Location(x));

    public static readonly info = async (
        uuid: string,
        brand: string,
        location: string
    ) => {
        const data = (await axios
            .get(environment.api_url + "/" + brand + "/location/" + location, {
                headers: {
                    "User-Token": uuid,
                },
            })
            .then((res) => (res.status === 200 ? res.data : undefined))
            .catch(console.error)) as LocationRaw | undefined;
        if (data) return new Location(data);
        return undefined;
    };

    public static readonly register = async (
        uuid: string,
        brand: string,
        name: string,
        city: string,
        address: string,
        mo: boolean,
        moStart: number,
        moEnd: number,
        tu: boolean,
        tuStart: number,
        tuEnd: number,
        we: boolean,
        weStart: number,
        weEnd: number,
        th: boolean,
        thStart: number,
        thEnd: number,
        fr: boolean,
        frStart: number,
        frEnd: number,
        sa: boolean,
        saStart: number,
        saEnd: number,
        su: boolean,
        suStart: number,
        suEnd: number,
        originalLang: string,
        image?: string
    ) => {
        const res = await axios.post(
            environment.api_url + "/" + brand + "/location",
            {
                name,
                city,
                address,
                mo,
                moStart,
                moEnd,
                tu,
                tuStart,
                tuEnd,
                we,
                weStart,
                weEnd,
                th,
                thStart,
                thEnd,
                fr,
                frStart,
                frEnd,
                sa,
                saStart,
                saEnd,
                su,
                suStart,
                suEnd,
                originalLang,
                image,
            },
            {
                headers: {
                    "User-Token": uuid,
                },
            }
        );
        return { key: res.data.key };
    };

    public static readonly update = async (
        uuid: string,
        brand: string,
        location: string,
        name: string,
        city: string,
        address: string,
        timezone: string,
        currency: string,
        mo: boolean,
        moStart: number,
        moEnd: number,
        tu: boolean,
        tuStart: number,
        tuEnd: number,
        we: boolean,
        weStart: number,
        weEnd: number,
        th: boolean,
        thStart: number,
        thEnd: number,
        fr: boolean,
        frStart: number,
        frEnd: number,
        sa: boolean,
        saStart: number,
        saEnd: number,
        su: boolean,
        suStart: number,
        suEnd: number,
        originalLang: string,
        image?: string | null,
        firstUpdate?: boolean,
        monthlyRevenueGoal?: number | null,
        monthlyHoursGoal?: number | null
    ) => {
        return axios.put(
            environment.api_url + "/" + brand + "/location/" + location,
            {
                name,
                city,
                address,
                timezone,
                currency,
                mo,
                moStart,
                moEnd,
                tu,
                tuStart,
                tuEnd,
                we,
                weStart,
                weEnd,
                th,
                thStart,
                thEnd,
                fr,
                frStart,
                frEnd,
                sa,
                saStart,
                saEnd,
                su,
                suStart,
                suEnd,
                originalLang,
                image,
                firstUpdate,
                monthlyRevenueGoal,
                monthlyHoursGoal,
            },
            {
                headers: {
                    "User-Token": uuid,
                },
            }
        );
    };

    public static readonly updateWeek = async (
        uuid: string,
        brand: string,
        location: string,
        timezone: string,
        currency: string,
        mo: boolean,
        tu: boolean,
        we: boolean,
        th: boolean,
        fr: boolean,
        sa: boolean,
        su: boolean
    ) =>
        axios.put(
            environment.api_url + "/" + brand + "/location/" + location,
            {
                timezone,
                currency,
                mo,
                tu,
                we,
                th,
                fr,
                sa,
                su,
            },
            {
                headers: {
                    "User-Token": uuid,
                },
            }
        );

    private static readonly _bookings = async (
        uuid: string,
        brand: string,
        location: string,
        year: number,
        month: number,
        day?: number,
        week?: boolean
    ) =>
        (await axios
            .get(
                environment.api_url +
                    "/" +
                    brand +
                    "/location/" +
                    location +
                    "/bookings/" +
                    year +
                    "/" +
                    month +
                    (day !== undefined ? "/" + day : ""),
                {
                    headers: {
                        "User-Token": uuid,
                    },
                    params:
                        week === true
                            ? {
                                  week: true,
                              }
                            : undefined,
                }
            )
            .then((res) => (res.status === 200 ? res.data : undefined))
            .catch(console.error)) as
            | DayCountSummary[]
            | DayCountSummary
            | undefined;

    public static readonly bookingsMonth = async (
        uuid: string,
        brand: string,
        location: string,
        year: number,
        month: number
    ) =>
        ((await this._bookings(uuid, brand, location, year, month)) ??
            []) as DayCountSummary[];

    public static readonly bookingsWeek = async (
        uuid: string,
        brand: string,
        location: string,
        year: number,
        month: number,
        day: number
    ) =>
        ((await this._bookings(
            uuid,
            brand,
            location,
            year,
            month,
            day,
            true
        )) ?? []) as DayCountSummary[];

    public static readonly bookingsDay = async (
        uuid: string,
        brand: string,
        location: string,
        year: number,
        month: number,
        day: number
    ) =>
        ((await this._bookings(
            uuid,
            brand,
            location,
            year,
            month,
            day
        )) ?? { count: -1 }) as DayCountSummary;
}
